// Copyright (c) 2026 Michael F. Kamprath
//
// quiz-mode.js -- Async state machine that drives Quiz-mode rounds.
//
// Sibling to learn-mode.js.  Implements the state chart from
// docs/QUIZ-MODE-LEGACY-SPEC §1:
//
//   SCENE_SETUP -> ENTRANCE_ANIMATION -> QUIZ_PROMPT_INTRO
//     -> QUIZ_PLAQUE_ENTRANCE -> AWAITING_ANSWER
//     ├─ wrong tap -> INCORRECT_FEEDBACK -> AWAITING_ANSWER (loop)
//     ├─ 30 s idle -> PLAYER_ASSIST_PROMPT -> AWAITING_ANSWER (loop)
//     └─ correct tap -> CORRECT_FEEDBACK -> POST_ANSWER_DELAY
//                    -> SCENE_TEARDOWN -> SCENE_SETUP (next round)
//
// The caller supplies an async `startRound` callback (typically built
// in app.js) that:
//   1. Tears down the previous scene if one exists.
//   2. Builds the new scene for the given theme + object count, incl.
//      the choice-plaque layer and tap wiring.
//   3. Returns a QuizRound handle with the plaqueManager interface
//      the machine drives (see typedef below).
//
// PixiJS is never touched here -- the plaqueManager is opaque.  Only
// the deps + the plaqueManager's method contract need to exist for
// unit tests to drive the full state chart.
//
// Cancellation: start() creates a PlaybackToken; stop() cancels it and
// awaits the run loop.  Every await boundary checks token.cancelled
// and bails before state transitions advance, so a mid-round quit
// unwinds cleanly without stray audio / animations leaking into the
// next session.

/**
 * @typedef {Object} QuizPlaqueManager
 *
 * Opaque interface the state machine drives.  Implemented by the
 * caller (app.js) on top of the PixiJS choice-plaque layer.
 *
 * @property {(correctValue: number, token: object) => Promise<void>} addPlaques
 *   Build and animate in all choice plaques for this round.  Resolves
 *   when the last plaque's entrance animation finishes.  The machine
 *   hooks tap observation BEFORE awaiting this, so early taps during
 *   entrance are delivered in order.
 *
 * @property {(cb: (evt: {
 *     plaqueId: string,
 *     value: number,
 *     correct: boolean,
 *   }) => void) => () => void} onTap
 *   Subscribe to plaque-tap events.  Returns an unsubscribe fn.  The
 *   manager is responsible for suppressing taps on already-selected
 *   plaques (spec §4) -- the machine trusts every event it receives.
 *
 * @property {(plaqueId: string) => Promise<void>} playCorrectAnimation
 *   Three-phase rotation-grow celebration on the correct plaque
 *   (spec §4, ~0.95 s).
 *
 * @property {(plaqueId: string) => Promise<void>} playIncorrectAnimation
 *   Dim the tapped wrong plaque (spec §4, 0.4 s).
 *
 * @property {() => Promise<void>} dimAllUnselected
 *   After a correct tap, dim every other plaque that hasn't been
 *   tapped yet (spec §2 CORRECT_FEEDBACK step 1).
 */

/**
 * @typedef {Object} QuizRound
 * @property {QuizPlaqueManager} plaqueManager
 * @property {Promise<void>} whenEntranceDone
 *   Resolves when the scene's entrance animation has completed (objects
 *   visible, idle animations armed).  The machine blocks on this before
 *   the quiz-intro prompt plays.
 * @property {() => Promise<void>} teardown
 *   Destroys the scene, plaques, animations.  Safe to call repeatedly.
 */

/**
 * @typedef {Object} QuizModeDeps
 * @property {import('./theme-pool.js')} themePool
 *   Round-rotation theme source (must implement draw()).  Shared with
 *   learn-mode in practice; the spec §6 and §11-Q1 confirm quiz uses
 *   the same themeMaxRepeat.
 * @property {() => number} getMaxCount
 *   Max object count setting; used with pickObjectCount.
 * @property {() => Object|null} pickVoiceSet
 *   Returns the voice set to use for the next round.
 * @property {Object} gameAudio
 *   Object returned by createGameAudio.  Must expose playQuizIntro,
 *   playQuizSuccess, playIncorrectFeedback, playPlayerAssistPrompt,
 *   playNewThemeIntro, playInterjection.  (playQuizSessionStart fires
 *   from the home screen before the machine starts.)
 * @property {(ctx: {
 *     themeId: string,
 *     objectCount: number,
 *     voiceSet: Object,
 *     token: Object,
 *   }) => Promise<QuizRound | null>} startRound
 *   Caller-provided round builder; returns null to skip the round (e.g.
 *   theme data couldn't be loaded).
 * @property {() => { cancelled: boolean, cancel: () => void }} createToken
 *   PlaybackToken factory.
 * @property {(seconds: number) => Promise<void>} pause
 *   Sleep helper.  Used for inter-round breather and post-answer delay.
 * @property {(maxCount: number) => number} [pickObjectCount]
 *   How to choose an object count given the max-count setting.  Default:
 *   uniform random in [1, maxCount].
 * @property {number} [postAnswerDelay]
 *   Seconds between the success audio finishing and the next round
 *   starting.  Default 0.75 (spec §2, CountGameController.m:902).
 * @property {number} [interRoundDelay]
 *   Seconds of additional silence after teardown before the next round
 *   kicks off.  Default 0 -- the legacy app has no extra inter-round
 *   pause beyond the 0.75 s.  Exposed for test parity with learn-mode.
 * @property {number} [playerAssistTimeoutMs]
 *   Idle timeout before the player-assist prompt fires.  Default 30 000
 *   (spec `kPlayerAssistTimeInterval`, CountGameController.m:737).
 * @property {(state: string, meta?: object) => void} [onState]
 *   Observer for state transitions (DEV logging + telemetry).
 * @property {() => number} [rng]
 *   RNG for object-count picking.  Defaults to Math.random.
 * @property {(fn: () => void, ms: number) => any} [setTimeoutImpl]
 *   Native setTimeout by default.  Injectable so tests can pump the
 *   player-assist timer without globally mocking the clock.
 * @property {(id: any) => void} [clearTimeoutImpl]
 *   Native clearTimeout by default.
 */

export const STATES = Object.freeze({
  IDLE:                  'IDLE',
  SCENE_SETUP:           'SCENE_SETUP',
  ENTRANCE_ANIMATION:    'ENTRANCE_ANIMATION',
  QUIZ_PROMPT_INTRO:     'QUIZ_PROMPT_INTRO',
  QUIZ_PLAQUE_ENTRANCE:  'QUIZ_PLAQUE_ENTRANCE',
  AWAITING_ANSWER:       'AWAITING_ANSWER',
  INCORRECT_FEEDBACK:    'INCORRECT_FEEDBACK',
  PLAYER_ASSIST_PROMPT:  'PLAYER_ASSIST_PROMPT',
  CORRECT_FEEDBACK:      'CORRECT_FEEDBACK',
  POST_ANSWER_DELAY:     'POST_ANSWER_DELAY',
  SCENE_TEARDOWN:        'SCENE_TEARDOWN',
  STOPPED:               'STOPPED',
});

const noop = () => {};

/** 30 s idle window before the "count then touch the right number" prompt. */
const DEFAULT_PLAYER_ASSIST_TIMEOUT_MS = 30_000;

/** 0.75 s silence after the success audio before advancing to next round. */
const DEFAULT_POST_ANSWER_DELAY_SEC = 0.75;

function defaultPickObjectCount(maxCount, rng) {
  const clamped = Math.max(1, Math.min(20, Math.floor(maxCount || 1)));
  return Math.floor(rng() * clamped) + 1;
}

/**
 * Build the tap-answer watcher for one AWAITING_ANSWER phase.
 *
 * Responsibilities:
 *   - Subscribe to plaqueManager.onTap.
 *   - Serialize wrong-tap feedback (animation + audio) so rapid taps
 *     queue in order and don't overlap audio (legacy _gameSequence
 *     behaviour).
 *   - Arm the player-assist timer and reset it on every tap.
 *   - Resolve the watcher's promise with the correct-tap event.
 *
 * The watcher is created BEFORE the plaque entrance animation so taps
 * that land during entrance are captured (plaque manager decides
 * whether to deliver them immediately or defer).
 *
 * @returns {{ promise: Promise<object>, cancel: () => void }}
 */
function createAnswerWaiter({
  plaqueManager, gameAudio,
  themeId, voiceSet, correctValue, objectCount,
  playerAssistTimeoutMs,
  setState, setTimeoutImpl, clearTimeoutImpl,
  token,
}) {
  let done = false;
  let resolveCorrect;
  const promise = new Promise((r) => { resolveCorrect = r; });

  // Serialize wrong-tap handling so rapid taps don't fire overlapping
  // incorrect-feedback audio / animation.  Mirrors legacy _gameSequence.
  let wrongTapChain = Promise.resolve();

  let timerHandle = null;
  const clearAssistTimer = () => {
    if (timerHandle != null) {
      clearTimeoutImpl(timerHandle);
      timerHandle = null;
    }
  };
  const armAssistTimer = () => {
    if (done || token.cancelled) return;
    clearAssistTimer();
    timerHandle = setTimeoutImpl(() => {
      timerHandle = null;
      if (done || token.cancelled) return;
      // Fire the assist prompt sequentially after whatever wrong-tap
      // handling is currently in flight, so it doesn't overlap with
      // in-progress comparator audio.  After the prompt plays, the
      // AWAITING_ANSWER state is re-entered and the timer re-armed.
      wrongTapChain = wrongTapChain.then(async () => {
        if (done || token.cancelled) return;
        setState(STATES.PLAYER_ASSIST_PROMPT, { themeId, objectCount });
        try {
          await gameAudio.playPlayerAssistPrompt(
            themeId, voiceSet, objectCount, token,
          );
        } catch {
          // Audio helpers swallow prompt-resolution misses; defensive catch
          // here keeps the loop alive even if the helper rejects for any
          // other reason.
        }
        if (done || token.cancelled) return;
        setState(STATES.AWAITING_ANSWER, { themeId, objectCount });
        armAssistTimer();
      });
    }, playerAssistTimeoutMs);
  };

  // Counter for the round_played telemetry event.  Incremented on
  // every wrong plaque selection; read by the caller after the
  // watcher resolves with the correct answer.
  let wrongTaps = 0;

  const unsubscribe = plaqueManager.onTap((evt) => {
    if (done || token.cancelled) return;

    // Every tap resets the idle timer -- the player is engaged.
    // The timer is re-armed after the tap's feedback completes so a
    // 30 s gap starts counting from when the scene settles again.
    clearAssistTimer();

    if (evt.correct) {
      done = true;
      resolveCorrect(evt);
      return;
    }

    wrongTaps += 1;
    wrongTapChain = wrongTapChain.then(async () => {
      if (done || token.cancelled) return;
      setState(STATES.INCORRECT_FEEDBACK, {
        themeId, plaqueId: evt.plaqueId, value: evt.value, correctValue,
      });
      try {
        await plaqueManager.playIncorrectAnimation(evt.plaqueId);
      } catch { /* keep loop alive */ }
      if (done || token.cancelled) return;
      try {
        await gameAudio.playIncorrectFeedback(
          themeId, correctValue, evt.value, voiceSet, token,
        );
      } catch { /* keep loop alive */ }
      if (done || token.cancelled) return;
      setState(STATES.AWAITING_ANSWER, { themeId, objectCount });
      armAssistTimer();
    });
  });

  armAssistTimer();

  return {
    promise,
    getWrongTaps: () => wrongTaps,
    cancel() {
      done = true;
      clearAssistTimer();
      try { unsubscribe(); } catch { /* already torn down */ }
    },
  };
}

/**
 * Create a quiz-mode state machine.  Dormant until start() is called.
 * stop() cancels mid-round (tears down the scene + plaques via
 * round.teardown() and exits the loop).
 *
 * @param {QuizModeDeps} deps
 */
export function createQuizModeMachine(deps) {
  const {
    themePool,
    getMaxCount,
    pickVoiceSet,
    gameAudio,
    startRound,
    createToken,
    pause,
    pickObjectCount,
    postAnswerDelay = DEFAULT_POST_ANSWER_DELAY_SEC,
    interRoundDelay = 0,
    playerAssistTimeoutMs = DEFAULT_PLAYER_ASSIST_TIMEOUT_MS,
    onState = noop,
    rng = Math.random,
    setTimeoutImpl = (fn, ms) => setTimeout(fn, ms),
    clearTimeoutImpl = (id) => clearTimeout(id),
    // Telemetry: optional round-end emit.  Fired with completed=true
    // when the child picks the correct plaque, completed=false when
    // the session is cancelled mid-round.  first_try = (wrongTaps===0).
    trackRoundPlayed = noop,
  } = deps;

  if (!themePool || typeof themePool.draw !== 'function') {
    throw new TypeError('createQuizModeMachine: themePool with draw() required');
  }
  if (typeof getMaxCount !== 'function') {
    throw new TypeError('createQuizModeMachine: getMaxCount function required');
  }
  if (typeof pickVoiceSet !== 'function') {
    throw new TypeError('createQuizModeMachine: pickVoiceSet function required');
  }
  if (!gameAudio) {
    throw new TypeError('createQuizModeMachine: gameAudio required');
  }
  if (typeof startRound !== 'function') {
    throw new TypeError('createQuizModeMachine: startRound function required');
  }
  if (typeof createToken !== 'function') {
    throw new TypeError('createQuizModeMachine: createToken function required');
  }
  if (typeof pause !== 'function') {
    throw new TypeError('createQuizModeMachine: pause function required');
  }

  const pickCount = typeof pickObjectCount === 'function'
    ? pickObjectCount
    : (mc) => defaultPickObjectCount(mc, rng);

  let running = false;
  let sessionToken = null;
  let lastState = STATES.IDLE;
  /** @type {QuizRound | null} */
  let currentRound = null;
  /** @type {Promise<void> | null} */
  let runPromise = null;
  // Cancellation signal: stop() fulfills cancelPromise so any long
  // round-level await (entrance, plaque entrance, answer wait, pauses)
  // can unblock immediately instead of waiting for a promise that will
  // never resolve after cancellation.  Mirrors learn-mode.js's pattern.
  let cancelResolve = null;
  /** @type {Promise<void> | null} */
  let cancelPromise = null;

  function armCancel() {
    cancelPromise = new Promise((r) => { cancelResolve = r; });
  }

  function cancellable(p) {
    return cancelPromise ? Promise.race([p, cancelPromise]) : p;
  }

  function setState(state, meta) {
    lastState = state;
    try { onState(state, meta); }
    catch (err) {
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.error('[quiz-mode] onState threw:', err);
      }
    }
  }

  /**
   * @type {{ themeId: string, correctValue: number, watcher: any } | null}
   * In-flight round metadata for round_played telemetry.  Set after
   * startRound + watcher creation; cleared at CORRECT_FEEDBACK by
   * emitRoundCompleted().  If it survives the loop break, the post-
   * loop guard fires completed=false.
   */
  let roundInFlight = null;

  function emitRoundCompleted() {
    if (!roundInFlight) return;
    const wrong = roundInFlight.watcher?.getWrongTaps?.() ?? 0;
    try {
      trackRoundPlayed({
        mode: 'quiz',
        theme: roundInFlight.themeId,
        count_value: roundInFlight.correctValue,
        completed: true,
        side_game_engaged: !!currentRound?.sideGameEngaged?.(),
        object_taps: Number.isFinite(currentRound?.objectTaps?.()) ? currentRound.objectTaps() : 0,
        bee_taps: Number.isFinite(currentRound?.beeTaps?.()) ? currentRound.beeTaps() : 0,
        first_try: wrong === 0,
        wrong_selections: wrong,
      });
    } catch { /* non-fatal */ }
    roundInFlight = null;
  }

  function emitRoundAbandonedIfAny() {
    if (!roundInFlight) return;
    const wrong = roundInFlight.watcher?.getWrongTaps?.() ?? 0;
    const sideGame = typeof roundInFlight.sideGameEngaged === 'boolean'
      ? roundInFlight.sideGameEngaged
      : !!currentRound?.sideGameEngaged?.();
    const beeTaps = Number.isFinite(roundInFlight.beeTaps)
      ? roundInFlight.beeTaps
      : (Number.isFinite(currentRound?.beeTaps?.()) ? currentRound.beeTaps() : 0);
    const objectTaps = Number.isFinite(roundInFlight.objectTaps)
      ? roundInFlight.objectTaps
      : (Number.isFinite(currentRound?.objectTaps?.()) ? currentRound.objectTaps() : 0);
    try {
      trackRoundPlayed({
        mode: 'quiz',
        theme: roundInFlight.themeId,
        count_value: roundInFlight.correctValue,
        completed: false,
        side_game_engaged: sideGame,
        object_taps: objectTaps,
        bee_taps: beeTaps,
        // First-try is nominally null for Learn but Quiz ran the
        // selection loop; record whether they got zero wrong taps
        // so far so the metric remains consistent with the spec's
        // "true if correct on first selection" definition.  Since
        // they never selected the CORRECT plaque, first_try is
        // always false for an abandoned Quiz round.
        first_try: false,
        wrong_selections: wrong,
      });
    } catch { /* non-fatal */ }
    roundInFlight = null;
  }

  async function teardownCurrent() {
    // Snapshot side-game flag BEFORE releasing the round handle so a
    // post-loop abandonment emit still sees the value even after the
    // round has been torn down.
    if (roundInFlight && typeof currentRound?.sideGameEngaged === 'function') {
      roundInFlight.sideGameEngaged = !!currentRound.sideGameEngaged();
    }
    if (roundInFlight && typeof currentRound?.beeTaps === 'function') {
      const n = currentRound.beeTaps();
      roundInFlight.beeTaps = Number.isFinite(n) ? n : 0;
    }
    if (roundInFlight && typeof currentRound?.objectTaps === 'function') {
      const n = currentRound.objectTaps();
      roundInFlight.objectTaps = Number.isFinite(n) ? n : 0;
    }
    if (!currentRound) return;
    const round = currentRound;
    currentRound = null;
    try {
      await round.teardown();
    } catch (err) {
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[quiz-mode] round teardown threw:', err);
      }
    }
  }

  async function runSession() {
    const token = sessionToken;
    setState(STATES.SCENE_SETUP, { sessionStart: true });

    while (!token.cancelled) {
      const themeId = themePool.draw();
      if (!themeId) {
        if (import.meta.env?.DEV) {
          // eslint-disable-next-line no-console
          console.warn('[quiz-mode] themePool.draw() returned null; exiting session');
        }
        break;
      }
      const maxCount = getMaxCount();
      const objectCount = pickCount(maxCount);
      const voiceSet = pickVoiceSet();
      // Per spec §3 value generation, correctValue === objectCount.
      const correctValue = objectCount;

      setState(STATES.SCENE_SETUP, { themeId, objectCount });

      currentRound = await startRound({ themeId, objectCount, voiceSet, token });
      if (!currentRound) {
        // startRound refused (theme data load failed, scene can't be
        // built).  Yield a macrotask before retrying so a concurrent
        // stop() call has a chance to flip token.cancelled before the
        // next while-iteration -- otherwise the retry loop stays
        // entirely in microtasks and starves the event loop.
        await cancellable(pause(0));
        continue;
      }
      if (token.cancelled) { await teardownCurrent(); break; }

      setState(STATES.ENTRANCE_ANIMATION, { themeId, objectCount });
      await cancellable(currentRound.whenEntranceDone);
      if (token.cancelled) { await teardownCurrent(); break; }

      // "Let's count the sheep!" — same per-round theme intro as Learn
      // mode (reuses playNewThemeIntro).  Skipping when the helper is
      // absent keeps tests that only stub the quiz-specific methods
      // from crashing.
      if (typeof gameAudio.playNewThemeIntro === 'function') {
        await gameAudio.playNewThemeIntro(themeId, voiceSet, token);
        if (token.cancelled) { await teardownCurrent(); break; }
      }

      setState(STATES.QUIZ_PROMPT_INTRO, { themeId, objectCount });
      // "How many sheep do you see?"
      await gameAudio.playQuizIntro(themeId, voiceSet, objectCount, token);
      if (token.cancelled) { await teardownCurrent(); break; }

      // The assist-timer watcher subscribes to onTap immediately so
      // taps that land during plaque entrance are captured in order.
      setState(STATES.QUIZ_PLAQUE_ENTRANCE, { themeId, objectCount });
      const watcher = createAnswerWaiter({
        plaqueManager: currentRound.plaqueManager,
        gameAudio,
        themeId, voiceSet, correctValue, objectCount,
        playerAssistTimeoutMs,
        setState,
        setTimeoutImpl, clearTimeoutImpl,
        token,
      });
      // Register the round as in-flight once the watcher exists -- any
      // cancellation from this point forward that doesn't reach
      // CORRECT_FEEDBACK fires completed=false via the post-loop guard.
      roundInFlight = { themeId, correctValue, watcher };

      try {
        await cancellable(
          currentRound.plaqueManager.addPlaques(correctValue, token),
        );
        if (token.cancelled) { watcher.cancel(); await teardownCurrent(); break; }

        setState(STATES.AWAITING_ANSWER, { themeId, objectCount });
        const answer = await cancellable(watcher.promise);
        if (token.cancelled) { watcher.cancel(); await teardownCurrent(); break; }

        setState(STATES.CORRECT_FEEDBACK, {
          themeId, plaqueId: answer.plaqueId, correctValue,
        });
        // Telemetry: as soon as the child picks the correct plaque the
        // round has effectively succeeded.  Emit here so a tab-close
        // during the celebration animation / success audio doesn't
        // re-classify a completed round as abandoned.
        emitRoundCompleted();
        // Fire the correct-answer reveal SFX at the start of
        // CORRECT_FEEDBACK -- before the dim + celebration animations
        // so it lands on the tap beat, not after the 0.95 s rotation.
        // Optional chaining lets older fake gameAudio objects (unit
        // tests that predate the method) skip this safely.
        if (typeof gameAudio.playQuizCorrectReveal === 'function') {
          try { gameAudio.playQuizCorrectReveal(token); }
          catch { /* fire-and-forget; non-fatal */ }
        }
        // Dim non-selected plaques and play the correct celebration
        // in parallel (matches the spec §2 CORRECT_FEEDBACK steps 1+2
        // running simultaneously).
        try {
          await Promise.all([
            currentRound.plaqueManager.dimAllUnselected(),
            currentRound.plaqueManager.playCorrectAnimation(answer.plaqueId),
          ]);
        } catch {
          // Animation errors shouldn't derail the round.  The audio
          // below still plays and the round still advances.
        }
        if (token.cancelled) { watcher.cancel(); await teardownCurrent(); break; }

        await gameAudio.playQuizSuccess(voiceSet, token);
        if (token.cancelled) { watcher.cancel(); await teardownCurrent(); break; }

        setState(STATES.POST_ANSWER_DELAY, { themeId, objectCount });
        if (postAnswerDelay > 0) {
          await cancellable(pause(postAnswerDelay));
          if (token.cancelled) { watcher.cancel(); await teardownCurrent(); break; }
        }
      } finally {
        watcher.cancel();
      }

      setState(STATES.SCENE_TEARDOWN, { themeId, objectCount });
      await teardownCurrent();

      if (interRoundDelay > 0) {
        await cancellable(pause(interRoundDelay));
        if (token.cancelled) break;
      }
    }

    // If cancellation broke the loop before CORRECT_FEEDBACK, the
    // round is still in-flight -- emit completed=false with whatever
    // wrong-selection count accumulated during the failed attempts.
    emitRoundAbandonedIfAny();
    await teardownCurrent();
    setState(STATES.STOPPED, { cancelled: token.cancelled });
  }

  return {
    get state() {
      return lastState;
    },

    /**
     * Begin the session.  Idempotent: calling twice returns the same
     * promise until stop() resolves.
     *
     * @returns {Promise<void>}  Resolves when the session exits.
     */
    start() {
      if (running) return runPromise;
      running = true;
      sessionToken = createToken();
      armCancel();
      lastState = STATES.IDLE;
      runPromise = runSession().finally(() => {
        running = false;
        cancelPromise = null;
        cancelResolve = null;
      });
      return runPromise;
    },

    /**
     * Cancel the session mid-round.  Awaits the run loop's exit.
     */
    async stop() {
      if (!running) return;
      if (sessionToken) sessionToken.cancel();
      if (cancelResolve) cancelResolve();
      try { await runPromise; }
      catch { /* already logged inside runSession */ }
    },

    isRunning() { return running; },
  };
}
